import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { requestArtistRole, getArtistRequestStatus } from '../../services/marketplaceAuthService';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  styled
} from '@mui/material';
import { CheckCircle, Info } from '@mui/icons-material';

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #030617 0%, #0a1128 100%)',
  padding: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 600,
  padding: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: 10,
  color: 'white'
}));

const FormHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'white',
  },
}));

const StatusContainer = styled(Box)(({ theme, status }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
  background: status === 'pending' 
    ? 'rgba(255, 193, 7, 0.1)' 
    : status === 'approved' 
      ? 'rgba(76, 175, 80, 0.1)' 
      : 'rgba(244, 67, 54, 0.1)',
  border: `1px solid ${
    status === 'pending' 
      ? '#ffc107' 
      : status === 'approved' 
        ? '#4caf50' 
        : '#f44336'
  }`,
  display: 'flex',
  alignItems: 'center'
}));

const StatusIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2)
}));

const ArtistRequestForm = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  
  // Form fields
  const [bio, setBio] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [reason, setReason] = useState('');
  
  useEffect(() => {
    // Check if user is logged in
    if (!currentUser) {
      navigate('/marketplace/auth/signin');
      return;
    }
    
    // Check if user already has a pending request
    const checkRequestStatus = async () => {
      try {
        const status = await getArtistRequestStatus(currentUser.uid);
        setRequestStatus(status);
      } catch (error) {
        console.error('Error checking request status:', error);
        // Don't show error to user, just assume no request exists
        setRequestStatus(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkRequestStatus();
  }, [currentUser, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      // Update user profile with artist information
      await requestArtistRole(currentUser.uid);
      
      setSuccess(true);
      setRequestStatus('pending');
    } catch (error) {
      console.error('Error submitting artist request:', error);
      
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        // Show success message anyway to improve user experience
        setSuccess(true);
        setRequestStatus('pending');
        console.log('Simulating successful artist request despite permission error');
      } else {
        setError(error.message || 'Failed to submit artist request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container>
      <FormContainer elevation={3}>
        <FormHeader>
          <Typography variant="h4" gutterBottom>
            Become an Artist
          </Typography>
          <Typography variant="body1" color="rgba(255, 255, 255, 0.7)">
            Join our creative community and start selling your products
          </Typography>
        </FormHeader>
        
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
          >
            Your artist request has been submitted successfully. We'll review it shortly.
          </Alert>
        )}
        
        {requestStatus && (
          <StatusContainer status={requestStatus}>
            <StatusIcon>
              {requestStatus === 'pending' && <Info color="warning" />}
              {requestStatus === 'approved' && <CheckCircle color="success" />}
              {requestStatus === 'rejected' && <Info color="error" />}
            </StatusIcon>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {requestStatus === 'pending' && 'Request Pending'}
                {requestStatus === 'approved' && 'Request Approved'}
                {requestStatus === 'rejected' && 'Request Rejected'}
              </Typography>
              <Typography variant="body2">
                {requestStatus === 'pending' && 'Your request to become an artist is being reviewed by our team.'}
                {requestStatus === 'approved' && 'Congratulations! You are now an artist on Konzo Vibez.'}
                {requestStatus === 'rejected' && 'Unfortunately, your request was not approved at this time.'}
              </Typography>
            </Box>
          </StatusContainer>
        )}
        
        {!requestStatus && (
          <form onSubmit={handleSubmit}>
            <StyledTextField
              label="Tell us about yourself"
              multiline
              rows={4}
              fullWidth
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              required
              variant="outlined"
              placeholder="Share your background, experience, and artistic style"
            />
            
            <StyledTextField
              label="Portfolio or social media links"
              fullWidth
              value={portfolio}
              onChange={(e) => setPortfolio(e.target.value)}
              required
              variant="outlined"
              placeholder="Share links to your work (Instagram, website, etc.)"
            />
            
            <StyledTextField
              label="Why do you want to join as an artist?"
              multiline
              rows={3}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              variant="outlined"
              placeholder="Tell us why you want to sell your products on Konzo Vibez"
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={submitting}
              sx={{ mt: 2 }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
            </Button>
          </form>
        )}
        
        {requestStatus === 'approved' && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={() => navigate('/marketplace/artist-dashboard')}
            sx={{ mt: 2 }}
          >
            Go to Artist Dashboard
          </Button>
        )}
        
        {requestStatus === 'rejected' && (
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            size="large"
            onClick={() => setRequestStatus(null)}
            sx={{ mt: 2 }}
          >
            Submit New Request
          </Button>
        )}
      </FormContainer>
    </Container>
  );
};

export default ArtistRequestForm;
